import * as i0 from '@angular/core';
import { Directive, HostBinding, EventEmitter, Component, ChangeDetectionStrategy, ContentChild, Input, Output, NgModule } from '@angular/core';
import { coerceBooleanProperty } from '@angular/cdk/coercion';
import * as i1 from '@angular/common';
import { CommonModule } from '@angular/common';
import * as i2 from '@paysafe-ui/components/icon';
import { PsIconModule } from '@paysafe-ui/components/icon';
import * as i3 from '@paysafe-ui/components/button';
import { PsButtonModule } from '@paysafe-ui/components/button';

/** Directive that is used to wrap custom notification content. */
class PsNotificationContentDirective {
    get psNotificationContent() {
        return true;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.12", ngImport: i0, type: PsNotificationContentDirective, deps: [], target: i0.ɵɵFactoryTarget.Directive }); }
    static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "17.3.12", type: PsNotificationContentDirective, selector: "[ps-notification-content], ps-notification-content", host: { properties: { "class.ps-notification-content": "this.psNotificationContent" } }, ngImport: i0 }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.12", ngImport: i0, type: PsNotificationContentDirective, decorators: [{
            type: Directive,
            args: [{ selector: '[ps-notification-content], ps-notification-content' }]
        }], propDecorators: { psNotificationContent: [{
                type: HostBinding,
                args: ['class.ps-notification-content']
            }] } });
/**
 * Directive that is used for providing a custom title template
 * for the notification.
 */
class PsNotificationTitleDirective {
    constructor(template) {
        this.template = template;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.12", ngImport: i0, type: PsNotificationTitleDirective, deps: [{ token: i0.TemplateRef }], target: i0.ɵɵFactoryTarget.Directive }); }
    static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "17.3.12", type: PsNotificationTitleDirective, selector: "[psNotificationTitle]", ngImport: i0 }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.12", ngImport: i0, type: PsNotificationTitleDirective, decorators: [{
            type: Directive,
            args: [{ selector: '[psNotificationTitle]' }]
        }], ctorParameters: () => [{ type: i0.TemplateRef }] });
/**
 * Directive that is used for providing a custom subtitle template
 * for the notification.
 */
class PsNotificationSubtitleDirective {
    constructor(template) {
        this.template = template;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.12", ngImport: i0, type: PsNotificationSubtitleDirective, deps: [{ token: i0.TemplateRef }], target: i0.ɵɵFactoryTarget.Directive }); }
    static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "17.3.12", type: PsNotificationSubtitleDirective, selector: "[psNotificationSubtitle]", ngImport: i0 }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.12", ngImport: i0, type: PsNotificationSubtitleDirective, decorators: [{
            type: Directive,
            args: [{ selector: '[psNotificationSubtitle]' }]
        }], ctorParameters: () => [{ type: i0.TemplateRef }] });

var PsNotificationType;
(function (PsNotificationType) {
    PsNotificationType["INFO"] = "info";
    PsNotificationType["SUCCESS"] = "success";
    PsNotificationType["WARNING"] = "warning";
    PsNotificationType["ERROR"] = "error";
})(PsNotificationType || (PsNotificationType = {}));

let nextUniqueId = 0;
class PsNotificationComponent {
    constructor() {
        this._dismissible = false;
        this._snackbar = false;
        this._disclaimer = false;
        this._vertical = false;
        /** A unique id for the item. If none is supplied, it will be auto-generated. */
        this.id = `ps-notification-${(nextUniqueId += 1)}`;
        /** Used to set the aria-label attribute on the list. */
        this.ariaLabel = '';
        /** Used to set the aria-labelledby attribute on the list. */
        this.ariaLabelledby = '';
        /**
         * Type of the notification.
         * Provides styles for the following types:
         * `'success'`, `'info'`, `'warning'`, `'error'`
         */
        this.type = PsNotificationType.INFO;
        /**
         * An event emitted when the close button is clicked.
         * It has no payload
         */
        this.close = new EventEmitter();
    }
    get role() {
        return 'alert';
    }
    get psNotification() {
        return true;
    }
    get psNotificationSuccess() {
        return this.type === PsNotificationType.SUCCESS;
    }
    get psNotificationWarning() {
        return this.type === PsNotificationType.WARNING;
    }
    get psNotificationError() {
        return this.type === PsNotificationType.ERROR;
    }
    get psNotificationInfo() {
        return this.type === PsNotificationType.INFO;
    }
    get psNotificationContentDevice() {
        return !!this.notificationContentTemplate;
    }
    /** Whether notification is used inside snackbar */
    get snackbar() {
        return this._snackbar;
    }
    set snackbar(value) {
        this._snackbar = coerceBooleanProperty(value);
    }
    /**
     * If `true`, alert can be dismissed by the user.
     *
     * The close button (×) will be displayed and you can be notified
     * of the event with the `(close)` output.
     */
    get dismissible() {
        return this._dismissible;
    }
    set dismissible(value) {
        this._dismissible = coerceBooleanProperty(value);
    }
    /**
     * If `true`, notification will become a disclaimer.
     * A disclaimer notification has specific styles
     * and can be attached to cards or tiles.
     */
    get disclaimer() {
        return this._disclaimer;
    }
    set disclaimer(value) {
        this._disclaimer = coerceBooleanProperty(value);
    }
    /**
     * If `true`, notification layout will be forced to vertical.
     * A vertical notification is useful within small containers and modals, similar to small screens
     */
    get vertical() {
        return this._vertical;
    }
    set vertical(value) {
        this._vertical = coerceBooleanProperty(value);
    }
    get icon() {
        switch (this.type) {
            case PsNotificationType.SUCCESS:
                return 'success-outline';
            case PsNotificationType.WARNING:
                return 'warning-outline';
            case PsNotificationType.ERROR:
                return 'error-outline';
            default:
                return 'info-outline';
        }
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.12", ngImport: i0, type: PsNotificationComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "17.3.12", type: PsNotificationComponent, selector: "ps-notification", inputs: { id: "id", ariaLabel: ["aria-label", "ariaLabel"], ariaLabelledby: ["aria-labelledby", "ariaLabelledby"], title: "title", subtitle: "subtitle", snackbar: "snackbar", dismissible: "dismissible", disclaimer: "disclaimer", vertical: "vertical", rounded: "rounded", type: "type" }, outputs: { close: "close" }, host: { properties: { "attr.id": "this.id", "attr.aria-label": "this.ariaLabel", "attr.aria-labelledby": "this.ariaLabelledby", "attr.role": "this.role", "class.ps-notification": "this.psNotification", "class.ps-notification-success": "this.psNotificationSuccess", "class.ps-notification-warning": "this.psNotificationWarning", "class.ps-notification-error": "this.psNotificationError", "class.ps-notification-info": "this.psNotificationInfo", "class.ps-notification-custom-content": "this.psNotificationContentDevice", "class.ps-notification-snackbar": "this.snackbar", "class.ps-notification-is-dismissible": "this.dismissible", "class.ps-notification-disclaimer": "this.disclaimer", "class.ps-notification-vertical": "this.vertical", "attr.type": "this.type" } }, queries: [{ propertyName: "notificationContentTemplate", first: true, predicate: PsNotificationContentDirective, descendants: true }, { propertyName: "notificationTitleTemplate", first: true, predicate: PsNotificationTitleDirective, descendants: true, read: PsNotificationTitleDirective }, { propertyName: "notificationSubtitleTemplate", first: true, predicate: PsNotificationSubtitleDirective, descendants: true }], ngImport: i0, template: "<div class=\"ps-notification-left-side\" [class.dismissible-margin-right]=\"dismissible\">\n  <div class=\"ps-notification-icon\">\n    <ps-icon>{{ icon }}</ps-icon>\n  </div>\n\n  <div class=\"ps-notification-titles\">\n    <div class=\"ps-notification-title\">\n      <ng-container\n        *ngTemplateOutlet=\"title ? defaultTitle : notificationTitleTemplate?.template; context: { $implicit: title }\"\n      ></ng-container>\n    </div>\n    <div class=\"ps-notification-subtitle\">\n      <ng-container\n        *ngTemplateOutlet=\"subtitle ? defaultSubtitle : notificationSubtitleTemplate?.template; context: { $implicit: subtitle }\"\n      ></ng-container>\n    </div>\n  </div>\n</div>\n\n<div class=\"ps-notification-content-wrapper\" *ngIf=\"notificationContentTemplate\">\n  <ng-content select=\"[psNotificationContent], ps-notification-content\"></ng-content>\n</div>\n\n<div class=\"ps-notification-close-icon\" *ngIf=\"dismissible\">\n  <button type=\"button\" aria-label=\"Close notification\" ps-icon-button (click)=\"close.emit()\">\n    <ps-icon>close</ps-icon>\n  </button>\n</div>\n\n<ng-template #defaultTitle let-title>{{ title }}</ng-template>\n<ng-template #defaultSubtitle let-subtitle>{{ subtitle }}</ng-template>\n", styles: [":host{display:flex;padding:1rem;width:100%;position:relative}:host.ps-notification-snackbar{padding:0}:host.ps-notification-disclaimer{margin-top:-1rem;padding:1.5rem .5rem .5rem;border-radius:0 0 1rem 1rem;min-width:0}:host.ps-notification-disclaimer .ps-notification-left-side .ps-notification-icon{padding-right:.5rem}:host.ps-notification-disclaimer .ps-notification-left-side .ps-notification-icon ps-icon{width:1.25rem;height:1.25rem;font-size:1.25rem}:host .ps-notification-left-side{display:flex;width:100%}:host .ps-notification-left-side .ps-notification-icon{padding-right:1rem;display:flex;flex-direction:column;justify-content:flex-start}:host .ps-notification-left-side .ps-notification-icon ps-icon{width:2rem;height:2rem;font-size:2rem}:host .ps-notification-left-side .ps-notification-titles{flex-direction:column;display:flex;width:100%;justify-content:center}:host.ps-notification-custom-content .ps-notification-left-side{padding-right:1rem}:host .ps-notification-content-wrapper{display:flex;justify-content:flex-end}:host .ps-notification-close-icon{margin-left:1rem}:host .ps-notification-close-icon button{width:1.5rem;height:1.5rem}:host .ps-notification-close-icon button ps-icon{transition:transform .25s}@media (hover: hover){:host .ps-notification-close-icon button:hover ps-icon{transform:rotate(90deg)}}:host.ps-notification-vertical.ps-notification-custom-content{flex-wrap:wrap}:host.ps-notification-vertical.ps-notification-custom-content .ps-notification-content-wrapper{padding-top:1rem;display:flex;width:100%;justify-content:flex-start;padding-right:0}:host.ps-notification-vertical.ps-notification-custom-content .ps-notification-left-side{padding-right:0}:host.ps-notification-vertical.ps-notification-custom-content .ps-notification-left-side.dismissible-margin-right{margin-right:2rem}:host.ps-notification-vertical.ps-notification-custom-content .ps-notification-close-icon{position:absolute;right:1rem}@media all and (max-width: 600px){:host.ps-notification-custom-content{flex-wrap:wrap}:host.ps-notification-custom-content .ps-notification-content-wrapper{padding-top:1rem;display:flex;width:100%;justify-content:flex-start;padding-right:0}:host.ps-notification-custom-content .ps-notification-left-side{padding-right:0}:host.ps-notification-custom-content .ps-notification-left-side.dismissible-margin-right{margin-right:2rem}:host.ps-notification-custom-content .ps-notification-close-icon{position:absolute;right:1rem}}\n"], dependencies: [{ kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "directive", type: i1.NgTemplateOutlet, selector: "[ngTemplateOutlet]", inputs: ["ngTemplateOutletContext", "ngTemplateOutlet", "ngTemplateOutletInjector"] }, { kind: "component", type: i2.PsIconComponent, selector: "ps-icon", inputs: ["inline", "fontSet", "fontIcon", "color", "svgIcon", "size"], exportAs: ["psIcon"] }, { kind: "component", type: i3.PsButtonComponent, selector: "button[ps-primary-button], button[ps-secondary-button], button[ps-text-button],  button[ps-fab], button[ps-mini-fab], button[ps-icon-button], button[ps-label-icon-button],  button[ps-label-icon-supportive-button], button[ps-label-icon-secondary-button]", inputs: ["disabled", "isBlock", "iconButtonText"] }], changeDetection: i0.ChangeDetectionStrategy.OnPush }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.12", ngImport: i0, type: PsNotificationComponent, decorators: [{
            type: Component,
            args: [{ selector: 'ps-notification', changeDetection: ChangeDetectionStrategy.OnPush, template: "<div class=\"ps-notification-left-side\" [class.dismissible-margin-right]=\"dismissible\">\n  <div class=\"ps-notification-icon\">\n    <ps-icon>{{ icon }}</ps-icon>\n  </div>\n\n  <div class=\"ps-notification-titles\">\n    <div class=\"ps-notification-title\">\n      <ng-container\n        *ngTemplateOutlet=\"title ? defaultTitle : notificationTitleTemplate?.template; context: { $implicit: title }\"\n      ></ng-container>\n    </div>\n    <div class=\"ps-notification-subtitle\">\n      <ng-container\n        *ngTemplateOutlet=\"subtitle ? defaultSubtitle : notificationSubtitleTemplate?.template; context: { $implicit: subtitle }\"\n      ></ng-container>\n    </div>\n  </div>\n</div>\n\n<div class=\"ps-notification-content-wrapper\" *ngIf=\"notificationContentTemplate\">\n  <ng-content select=\"[psNotificationContent], ps-notification-content\"></ng-content>\n</div>\n\n<div class=\"ps-notification-close-icon\" *ngIf=\"dismissible\">\n  <button type=\"button\" aria-label=\"Close notification\" ps-icon-button (click)=\"close.emit()\">\n    <ps-icon>close</ps-icon>\n  </button>\n</div>\n\n<ng-template #defaultTitle let-title>{{ title }}</ng-template>\n<ng-template #defaultSubtitle let-subtitle>{{ subtitle }}</ng-template>\n", styles: [":host{display:flex;padding:1rem;width:100%;position:relative}:host.ps-notification-snackbar{padding:0}:host.ps-notification-disclaimer{margin-top:-1rem;padding:1.5rem .5rem .5rem;border-radius:0 0 1rem 1rem;min-width:0}:host.ps-notification-disclaimer .ps-notification-left-side .ps-notification-icon{padding-right:.5rem}:host.ps-notification-disclaimer .ps-notification-left-side .ps-notification-icon ps-icon{width:1.25rem;height:1.25rem;font-size:1.25rem}:host .ps-notification-left-side{display:flex;width:100%}:host .ps-notification-left-side .ps-notification-icon{padding-right:1rem;display:flex;flex-direction:column;justify-content:flex-start}:host .ps-notification-left-side .ps-notification-icon ps-icon{width:2rem;height:2rem;font-size:2rem}:host .ps-notification-left-side .ps-notification-titles{flex-direction:column;display:flex;width:100%;justify-content:center}:host.ps-notification-custom-content .ps-notification-left-side{padding-right:1rem}:host .ps-notification-content-wrapper{display:flex;justify-content:flex-end}:host .ps-notification-close-icon{margin-left:1rem}:host .ps-notification-close-icon button{width:1.5rem;height:1.5rem}:host .ps-notification-close-icon button ps-icon{transition:transform .25s}@media (hover: hover){:host .ps-notification-close-icon button:hover ps-icon{transform:rotate(90deg)}}:host.ps-notification-vertical.ps-notification-custom-content{flex-wrap:wrap}:host.ps-notification-vertical.ps-notification-custom-content .ps-notification-content-wrapper{padding-top:1rem;display:flex;width:100%;justify-content:flex-start;padding-right:0}:host.ps-notification-vertical.ps-notification-custom-content .ps-notification-left-side{padding-right:0}:host.ps-notification-vertical.ps-notification-custom-content .ps-notification-left-side.dismissible-margin-right{margin-right:2rem}:host.ps-notification-vertical.ps-notification-custom-content .ps-notification-close-icon{position:absolute;right:1rem}@media all and (max-width: 600px){:host.ps-notification-custom-content{flex-wrap:wrap}:host.ps-notification-custom-content .ps-notification-content-wrapper{padding-top:1rem;display:flex;width:100%;justify-content:flex-start;padding-right:0}:host.ps-notification-custom-content .ps-notification-left-side{padding-right:0}:host.ps-notification-custom-content .ps-notification-left-side.dismissible-margin-right{margin-right:2rem}:host.ps-notification-custom-content .ps-notification-close-icon{position:absolute;right:1rem}}\n"] }]
        }], propDecorators: { notificationContentTemplate: [{
                type: ContentChild,
                args: [PsNotificationContentDirective, { static: false }]
            }], notificationTitleTemplate: [{
                type: ContentChild,
                args: [PsNotificationTitleDirective, { read: PsNotificationTitleDirective, static: false }]
            }], notificationSubtitleTemplate: [{
                type: ContentChild,
                args: [PsNotificationSubtitleDirective, { static: false }]
            }], id: [{
                type: Input
            }, {
                type: HostBinding,
                args: ['attr.id']
            }], ariaLabel: [{
                type: Input,
                args: ['aria-label']
            }, {
                type: HostBinding,
                args: ['attr.aria-label']
            }], ariaLabelledby: [{
                type: Input,
                args: ['aria-labelledby']
            }, {
                type: HostBinding,
                args: ['attr.aria-labelledby']
            }], role: [{
                type: HostBinding,
                args: ['attr.role']
            }], psNotification: [{
                type: HostBinding,
                args: ['class.ps-notification']
            }], psNotificationSuccess: [{
                type: HostBinding,
                args: ['class.ps-notification-success']
            }], psNotificationWarning: [{
                type: HostBinding,
                args: ['class.ps-notification-warning']
            }], psNotificationError: [{
                type: HostBinding,
                args: ['class.ps-notification-error']
            }], psNotificationInfo: [{
                type: HostBinding,
                args: ['class.ps-notification-info']
            }], psNotificationContentDevice: [{
                type: HostBinding,
                args: ['class.ps-notification-custom-content']
            }], title: [{
                type: Input
            }], subtitle: [{
                type: Input
            }], snackbar: [{
                type: Input
            }, {
                type: HostBinding,
                args: ['class.ps-notification-snackbar']
            }], dismissible: [{
                type: Input
            }, {
                type: HostBinding,
                args: ['class.ps-notification-is-dismissible']
            }], disclaimer: [{
                type: Input
            }, {
                type: HostBinding,
                args: ['class.ps-notification-disclaimer']
            }], vertical: [{
                type: Input
            }, {
                type: HostBinding,
                args: ['class.ps-notification-vertical']
            }], rounded: [{
                type: Input
            }], type: [{
                type: Input
            }, {
                type: HostBinding,
                args: ['attr.type']
            }], close: [{
                type: Output
            }] } });

class PsNotificationModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.12", ngImport: i0, type: PsNotificationModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "17.3.12", ngImport: i0, type: PsNotificationModule, declarations: [PsNotificationComponent,
            PsNotificationContentDirective,
            PsNotificationTitleDirective,
            PsNotificationSubtitleDirective], imports: [CommonModule, PsIconModule, PsButtonModule], exports: [PsNotificationComponent,
            PsNotificationContentDirective,
            PsNotificationTitleDirective,
            PsNotificationSubtitleDirective] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "17.3.12", ngImport: i0, type: PsNotificationModule, imports: [CommonModule, PsIconModule, PsButtonModule] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.12", ngImport: i0, type: PsNotificationModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [CommonModule, PsIconModule, PsButtonModule],
                    declarations: [
                        PsNotificationComponent,
                        PsNotificationContentDirective,
                        PsNotificationTitleDirective,
                        PsNotificationSubtitleDirective
                    ],
                    exports: [
                        PsNotificationComponent,
                        PsNotificationContentDirective,
                        PsNotificationTitleDirective,
                        PsNotificationSubtitleDirective
                    ]
                }]
        }] });

/**
 * Generated bundle index. Do not edit.
 */

export { PsNotificationComponent, PsNotificationContentDirective, PsNotificationModule, PsNotificationSubtitleDirective, PsNotificationTitleDirective, PsNotificationType };
